import React, { useCallback, useMemo, useState } from 'react';
import type {
	WorkflowErrorsMap,
	Transition,
	Status,
	StartNodeLayout,
	GlobalLoopsContainerLayout,
	Coordinates,
	Transitions,
	ScopeType,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { START_NODE_ID } from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { DiagramDirectTransition, DiagramTransition } from './toPortInfo.tsx';
import type {
	DiagramNode,
	DrawCompletionCause,
	ExpandableItemReference,
	ItemReference,
	Positionable,
	ViewBoxRect,
} from './ListEdgeDetails.tsx';
import { useLatest } from './useLatest.tsx';
import { GLOBAL_LOOPS_CONTAINER_TYPE } from './directionKeys.tsx';
import { useControllable } from './useControllable.tsx';
import { DEFAULT_PAN, DEFAULT_ZOOM_LEVEL } from './ellipsis.tsx';
import { GlobalTransitionsCloseReasonProvider } from './GlobalTransitionsCloseReasonProvider.tsx';
import Diagram from './WorkflowDiagram.tsx';
import ListNavigationWithMove from './ListNavigation.tsx';
import {
	getTransitionConnectionInfo,
	toDiagramGlobalLoopsContainerPosition,
	toDiagramStartNodePosition,
	toDiagramStatuses,
	toDiagramTransitions,
} from './toDiagramGlobalLoopsContainerPosition.tsx';

export type Props = {
	statuses: Status[];
	transitions: Transitions;
	errors?: WorkflowErrorsMap;
	startNodeLayout: StartNodeLayout;
	globalLoopsContainerLayout?: GlobalLoopsContainerLayout;
	zoom?: number;
	isImmutable?: boolean;
	selectedItem?: ItemReference;
	panOffset?: Coordinates;
	collapseTransitionLabels?: boolean;
	shouldShowGlobalLoops?: boolean;
	workflowScope: ScopeType;
	onSelect?: (item?: ItemReference) => void;
	onNodeUpdate?: (node: DiagramNode) => void;
	onDrawCompleted?: (cause: DrawCompletionCause) => void;
	onViewBoxChanged?: (viewBox: ViewBoxRect) => void;
	onZoomUpdate?: (zoomLevel: number) => void;
	onPanUpdate?: (pan: Coordinates) => void;
	onTransitionUpdate?: (updated: Pick<Transition, 'id' | 'toRef' | 'links'>) => void;
	onTransitionCreate?: (updated: Pick<Transition, 'toRef' | 'links'>) => void;
	onGlobalLoopsContainerMove?: (position: GlobalLoopsContainerLayout) => void;
	onTargetChange?: (targeted: ItemReference | undefined) => void;
	onMinZoomChange?: (minZoom: number) => void;
	minimumZoom?: number;
};

const AccessibleDiagram = ({
	statuses,
	transitions,
	errors,
	startNodeLayout,
	globalLoopsContainerLayout,
	zoom,
	panOffset,
	isImmutable = false,
	selectedItem,
	onSelect,
	onDrawCompleted,
	onNodeUpdate,
	onViewBoxChanged,
	onZoomUpdate,
	onTransitionUpdate,
	onTransitionCreate,
	onPanUpdate,
	onGlobalLoopsContainerMove,
	onTargetChange,
	onMinZoomChange,
	collapseTransitionLabels = false,
	shouldShowGlobalLoops = false,
	workflowScope,
	minimumZoom,
}: Props): React.JSX.Element => {
	const [targetedItem, setTargetedItem] = useState<ItemReference>();
	const [movingItem, setMovingItem] = useState<Positionable | undefined>(undefined);
	const [expandedItem, setExpandedItem] = useState<ExpandableItemReference | undefined>(undefined);

	const latestOnSelect = useLatest(onSelect);
	const latestOnDrawCompleted = useLatest(onDrawCompleted);

	const latestOnNodeUpdate = useLatest(onNodeUpdate);
	const latestOnViewBoxChanged = useLatest(onViewBoxChanged);
	const latestOnTransitionUpdate = useLatest(onTransitionUpdate);
	const latestOnTransitionCreate = useLatest(onTransitionCreate);
	const latestOnGlobalLoopsContainerMove = useLatest(onGlobalLoopsContainerMove);
	const latestOnTargetChange = useLatest(onTargetChange);

	const handleTargetChange = useCallback(
		(target?: ItemReference) => {
			setTargetedItem(target);
			latestOnTargetChange.current?.(target);
		},
		[latestOnTargetChange],
	);

	const diagramTransitions = useMemo(
		() => toDiagramTransitions(transitions, errors, statuses),
		[errors, statuses, transitions],
	);

	const initialStatusId = diagramTransitions.find(
		(transition): transition is DiagramDirectTransition =>
			transition.type === 'direct' && transition.source === START_NODE_ID,
	)?.target;

	const diagramStatuses = useMemo(
		() => toDiagramStatuses(statuses, workflowScope, initialStatusId, errors),
		[errors, initialStatusId, statuses, workflowScope],
	);

	const startNodePosition = useMemo(
		() => toDiagramStartNodePosition(startNodeLayout),
		[startNodeLayout],
	);

	const globalLoopsContainerPosition = useMemo(() => {
		return toDiagramGlobalLoopsContainerPosition(globalLoopsContainerLayout);
	}, [globalLoopsContainerLayout]);

	const onRenderComplete = useCallback(() => {
		latestOnDrawCompleted.current?.('INITIAL');
	}, [latestOnDrawCompleted]);

	const onSelectItem = useCallback(
		(item: ItemReference | undefined) => {
			if (!item) {
				latestOnSelect.current?.(undefined);
				return;
			}

			latestOnSelect.current?.({
				id: item.id,
				type: item.type,
			});
		},
		[latestOnSelect],
	);

	const onItemMove = useCallback(
		(payload: Positionable) => {
			if (payload.type === GLOBAL_LOOPS_CONTAINER_TYPE) {
				latestOnGlobalLoopsContainerMove.current?.(payload.position);
			} else {
				latestOnNodeUpdate.current?.({ id: payload.id, layout: payload.position });
			}
		},
		[latestOnGlobalLoopsContainerMove, latestOnNodeUpdate],
	);

	const clearGlobalLoopsContainerPosition = useCallback(() => {
		latestOnGlobalLoopsContainerMove.current?.({});
	}, [latestOnGlobalLoopsContainerMove]);

	const handleTransitionCreateNew = useCallback(
		(transition: DiagramTransition) => {
			latestOnTransitionCreate.current?.(getTransitionConnectionInfo(transition));
		},
		[latestOnTransitionCreate],
	);

	const handleTransitionPortChangeNew = useCallback(
		(transition: DiagramTransition) => {
			latestOnTransitionUpdate.current?.({
				id: transition.id,
				...getTransitionConnectionInfo(transition),
			});
		},
		[latestOnTransitionUpdate],
	);

	const [currentPan, updatePan] = useControllable(panOffset, onPanUpdate, DEFAULT_PAN);

	const [currentZoom, updateZoom] = useControllable(zoom, onZoomUpdate, DEFAULT_ZOOM_LEVEL);

	const content = (
		<>
			<ListNavigationWithMove
				onExpandedChange={setExpandedItem}
				expandedItem={expandedItem}
				startNodePosition={startNodePosition}
				globalLoopsContainerPosition={globalLoopsContainerPosition}
				statuses={statuses}
				transitions={transitions}
				errors={errors}
				isImmutable={isImmutable}
				movingItem={movingItem}
				selectedItem={selectedItem}
				zoom={currentZoom}
				pan={currentPan}
				minZoom={minimumZoom}
				workflowScope={workflowScope}
				onSelect={latestOnSelect.current}
				onTargetChange={handleTargetChange}
				setMovingItem={setMovingItem}
				onItemMove={onItemMove}
				onZoomChange={updateZoom}
				onPanChange={updatePan}
			/>
			<Diagram
				isImmutable={isImmutable}
				transitions={diagramTransitions}
				startNodePosition={startNodePosition}
				globalLoopsContainerPosition={globalLoopsContainerPosition}
				onClearGlobalLoopsContainerPosition={clearGlobalLoopsContainerPosition}
				statuses={diagramStatuses}
				selectedItem={selectedItem ?? undefined}
				targetedItem={targetedItem}
				movingItem={movingItem}
				zoom={currentZoom}
				pan={currentPan}
				onViewBoxChange={latestOnViewBoxChanged.current}
				onZoomChange={updateZoom}
				onPanChange={updatePan}
				onRenderComplete={onRenderComplete}
				onItemMove={onItemMove}
				onMinZoomChange={onMinZoomChange}
				minimumZoom={minimumZoom}
				onTransitionCreate={handleTransitionCreateNew}
				onTransitionPortChange={handleTransitionPortChangeNew}
				onSelect={onSelectItem}
				collapseTransitionLabels={collapseTransitionLabels}
				shouldShowGlobalLoops={shouldShowGlobalLoops}
				expandedItem={expandedItem}
				onExpandedChange={setExpandedItem}
			/>
		</>
	);

	if (expVal('hix-9182_sprt_a11y_global_transition', 'isEnabled', false)) {
		return <GlobalTransitionsCloseReasonProvider>{content}</GlobalTransitionsCloseReasonProvider>;
	}

	return content;
};

export default AccessibleDiagram;
